/**
 * EnviarPruebaModal — Modal sm para enviar una notificación de prueba a los
 * dispositivos registrados (§8.3.2 ① / §8.3.5).
 *
 * Recibe `contenido` (probar sin guardar, desde el composer) o `campanaId`.
 * Si no hay usuarios de prueba, muestra un aviso con link a /notificaciones/pruebas.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, FlaskConical, Loader2, Smartphone, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { enviarPruebaFn, gestionarDispositivosPruebaFn, mensajeError } from './notificacionesApi';
import {
  fmtRelativo,
  type ContenidoPrueba,
  type ResultadoPruebaDispositivo,
  type UsuarioPrueba,
} from './tiposCampana';
import styles from './EnviarPruebaModal.module.css';

interface EnviarPruebaModalProps {
  open: boolean;
  onClose: () => void;
  contenido?: ContenidoPrueba;
  campanaId?: string;
}

export function EnviarPruebaModal({ open, onClose, contenido, campanaId }: EnviarPruebaModalProps) {
  const [cargando, setCargando] = useState(false);
  const [usuarios, setUsuarios] = useState<UsuarioPrueba[] | null>(null);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resultados, setResultados] = useState<ResultadoPruebaDispositivo[] | null>(null);
  const [errorEnvio, setErrorEnvio] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setResultados(null);
    setErrorEnvio(null);
    setErrorCarga(null);
    setCargando(true);
    let activo = true;
    gestionarDispositivosPruebaFn({ action: 'listar' })
      .then((res) => {
        if (activo) setUsuarios(res.data.testUsuarios ?? []);
      })
      .catch((err) => {
        if (activo) setErrorCarga(mensajeError(err, 'No se pudieron cargar los dispositivos de prueba.'));
      })
      .finally(() => {
        if (activo) setCargando(false);
      });
    return () => {
      activo = false;
    };
  }, [open]);

  const totalDispositivos = (usuarios ?? []).reduce((acc, u) => acc + u.dispositivos.length, 0);

  const enviar = async () => {
    setEnviando(true);
    setErrorEnvio(null);
    try {
      const res = contenido
        ? await enviarPruebaFn({ contenido })
        : await enviarPruebaFn({ campanaId: campanaId ?? '' });
      setResultados(res.data.resultados);
    } catch (err) {
      setErrorEnvio(mensajeError(err, 'No se pudo enviar la prueba.'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Enviar prueba" size="sm">
      <div className={styles.body}>
        <div className={styles.header}>
          <FlaskConical size={18} aria-hidden="true" />
          <h3 className={styles.title}>Enviar notificación de prueba</h3>
        </div>

        {cargando ? (
          <p className={styles.loading}>
            <Loader2 size={16} className={styles.spin} aria-hidden="true" />
            Cargando dispositivos de prueba…
          </p>
        ) : errorCarga ? (
          <p className={styles.error} role="alert">{errorCarga}</p>
        ) : usuarios && usuarios.length === 0 ? (
          <div className={styles.aviso}>
            <p>
              No hay usuarios de prueba registrados. Agregue al menos uno para poder validar
              la notificación en un teléfono real.
            </p>
            <Link to="/notificaciones/pruebas" className={styles.avisoLink}>
              Ir a Dispositivos de prueba →
            </Link>
          </div>
        ) : (
          <>
            <ul className={styles.lista}>
              {(usuarios ?? []).map((u) => (
                <li key={u.uid} className={styles.usuario}>
                  <div className={styles.usuarioInfo}>
                    <span className={styles.usuarioNombre}>{u.nombre}</span>
                    <span className={styles.usuarioCedula}>{u.cedula}</span>
                  </div>
                  <div className={styles.dispositivos}>
                    {u.dispositivos.length === 0 ? (
                      <span className={styles.sinDispositivos}>Sin dispositivos</span>
                    ) : (
                      u.dispositivos.map((d, i) => (
                        <span key={`${d.plataforma}-${i}`} className={styles.dispositivo}>
                          <Smartphone size={12} aria-hidden="true" />
                          {d.plataforma === 'ios' ? 'iOS' : 'Android'}
                          {d.actualizadoEn ? ` · ${fmtRelativo(d.actualizadoEn)}` : ''}
                        </span>
                      ))
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {resultados && (
              <ul className={styles.resultados} aria-live="polite">
                {resultados.map((r, i) => (
                  <li
                    key={`${r.plataforma}-${i}`}
                    className={r.ok ? styles.resultadoOk : styles.resultadoError}
                  >
                    {r.ok ? (
                      <CheckCircle2 size={14} aria-hidden="true" />
                    ) : (
                      <XCircle size={14} aria-hidden="true" />
                    )}
                    <span>
                      {r.plataforma === 'ios' ? 'iOS' : 'Android'} —{' '}
                      {r.ok ? 'aceptada' : r.error ?? 'error'}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {errorEnvio && (
              <p className={styles.error} role="alert">{errorEnvio}</p>
            )}

            <div className={styles.acciones}>
              <Button variant="secondary" onClick={onClose}>
                Cerrar
              </Button>
              <Button
                variant="primary"
                loading={enviando}
                disabled={totalDispositivos === 0}
                onClick={() => void enviar()}
              >
                <FlaskConical size={14} aria-hidden="true" />
                {resultados ? 'Reenviar prueba' : 'Enviar prueba'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
